import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MB Management section (session-32, Feb-14-2026)
 *
 * • Lists every MB in the registry (visible + hidden).
 * • Admin can Create, Hide or Unhide — never delete (chat history + reports
 *   must stay intact).
 * • Every mutation goes through a 3-step gate: password → Google Auth →
 *   confirm — same pattern as Clean Slate. The Google callback returns to
 *   /admin-settings?section=mb&mb_verified=1 which the effect below picks up
 *   to skip straight to the final confirm step.
 */
const MBManagementSection = ({ user }) => {
  const { user: authUser } = useAuth();
  const activeUser = user || authUser;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: pwd, 2: google, 3: confirm
  const [action, setAction] = useState('create'); // create | hide | unhide
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [intentToken, setIntentToken] = useState('');
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/mb-registry`, { params: { user_id: activeUser?.id } });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load MB list');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (activeUser?.id) load(); /* eslint-disable-next-line */ }, [activeUser?.id]);

  // Handle return-from-Google-callback — the callback lands on
  // /admin-settings?section=mb&mb_verified=1 and stashes the intent_token in
  // sessionStorage before redirect.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('mb_verified') === '1') {
      const stash = sessionStorage.getItem('mb_registry_intent');
      if (stash) {
        try {
          const parsed = JSON.parse(stash);
          setAction(parsed.action); setName(parsed.name);
          setIntentToken(parsed.token); setStep(3); setModalOpen(true);
        } catch {}
        sessionStorage.removeItem('mb_registry_intent');
      }
      // clean URL
      q.delete('mb_verified');
      const clean = window.location.pathname + (q.toString() ? '?' + q.toString() : '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const openModal = (act, mbName = '') => {
    setAction(act); setName(mbName); setStep(1);
    setPhone(''); setPassword(''); setIntentToken('');
    setModalOpen(true);
  };

  const submitStep1 = async () => {
    if (!phone.trim() || !password.trim() || !name.trim()) {
      toast.error('Phone, password and MB name are required');
      return;
    }
    setWorking(true);
    try {
      const res = await axios.post(`${API}/admin/mb-registry/init`, {
        phone: phone.trim(), password, action, name: name.trim().toUpperCase(),
      });
      setIntentToken(res.data.intent_token);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally { setWorking(false); }
  };

  const startGoogle = () => {
    // Stash intent so we can resume after the Google callback
    sessionStorage.setItem('mb_registry_intent', JSON.stringify({
      token: intentToken, action, name: name.trim().toUpperCase(),
    }));
    // Emergent Google Auth flow — user picks a Google account, then we're
    // sent back to /admin-mb-callback#session_id=… which finishes the
    // verify-google POST and bounces to /admin-settings?section=mb&mb_verified=1
    const back = `${window.location.origin}/admin-mb-callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(back)}`;
  };

  const submitStep3 = async () => {
    // Google verification is already done inside MBCallback — just confirm.
    setWorking(true);
    try {
      await axios.post(`${API}/admin/mb-registry/confirm`, { intent_token: intentToken });
      toast.success(`${action === 'create' ? 'Created' : action === 'hide' ? 'Hidden' : 'Unhidden'} ${name}`);
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setWorking(false); }
  };

  const nextMbSuggestion = () => {
    const nums = items.map(i => parseInt((i.name || '').replace(/\D/g, ''), 10)).filter(n => !isNaN(n));
    const max = nums.length ? Math.max(...nums) : 6;
    return `MB${max + 1}`;
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 sm:p-6" data-testid="mb-management-section">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800">MB Management</h2>
          <p className="text-xs text-slate-500 mt-0.5">Create new MBs or hide existing ones. Every change needs your password + Google verification. MBs are never deleted — chat + report history stays intact.</p>
        </div>
        <button
          onClick={() => openModal('create', nextMbSuggestion())}
          className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg whitespace-nowrap"
          data-testid="mb-create-btn"
        >+ New MB</button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-400 text-sm py-8 text-center">No MBs yet</div>
      ) : (
        <div className="divide-y">
          {items.map((mb) => (
            <div key={mb.id || mb.name} className="flex items-center justify-between py-3" data-testid={`mb-row-${mb.name}`}>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${mb.hidden ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{mb.name}</span>
                {mb.hidden && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700 uppercase">Hidden</span>}
              </div>
              <div className="flex items-center gap-2">
                {mb.hidden ? (
                  <button
                    onClick={() => openModal('unhide', mb.name)}
                    className="text-xs px-3 py-1.5 rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 font-semibold"
                    data-testid={`mb-unhide-${mb.name}`}
                  >Unhide</button>
                ) : (
                  <button
                    onClick={() => openModal('hide', mb.name)}
                    className="text-xs px-3 py-1.5 rounded-full border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 font-semibold"
                    data-testid={`mb-hide-${mb.name}`}
                  >Hide</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={() => !working && setModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="mb-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-800">
                {action === 'create' ? 'Create' : action === 'hide' ? 'Hide' : 'Unhide'} {name}
              </h3>
              <button onClick={() => !working && setModalOpen(false)} className="text-slate-400 text-xl leading-none">×</button>
            </div>
            <div className="text-[11px] text-slate-500 mb-4">Step {step} of 3 · {step === 1 ? 'Password' : step === 2 ? 'Google verification' : 'Confirm'}</div>

            {step === 1 && (
              <div className="space-y-3">
                {action === 'create' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">MB Name</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value.toUpperCase())}
                      placeholder="e.g. MB12"
                      className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                      data-testid="mb-modal-name"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Admin phone</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 1558089654"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                    data-testid="mb-modal-phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-indigo-500"
                    data-testid="mb-modal-password"
                  />
                </div>
                <button
                  onClick={submitStep1}
                  disabled={working}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded"
                  data-testid="mb-modal-step1-next"
                >{working ? 'Verifying…' : 'Verify password → Next'}</button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded p-3">
                  Password verified. Now sign in with the authorised admin Google account to authorise this action.
                </div>
                <button
                  onClick={startGoogle}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded"
                  data-testid="mb-modal-step2-google"
                >Continue with Google →</button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <div className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded p-3">
                  ✅ Google verified. Confirm to <strong>{action}</strong> <strong>{name}</strong>.
                </div>
                <button
                  onClick={submitStep3}
                  disabled={working}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded"
                  data-testid="mb-modal-step3-confirm"
                >{working ? 'Working…' : `Confirm ${action}`}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MBManagementSection;
